<script setup>
/**
 * PlatformTooltipIcon
 *
 * Reusable component that renders a help icon (ⓘ) with a popover containing
 * dynamic tooltip content fetched from the SuperAdmin-managed PlatformTooltips.
 *
 * Usage: <PlatformTooltipIcon identifier="contacts_index" />
 *
 * - If no tooltip is found for the given identifier, nothing is rendered.
 * - If the tooltip has a URL, a "Saiba mais" button is shown that opens in a new tab.
 * - Tooltip data is fetched once on app init and cached in the Vuex store.
 */
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  identifier: {
    type: String,
    required: true,
  },
  size: {
    type: String,
    default: 'md',
    validator: v => ['sm', 'md', 'lg'].includes(v),
  },
});

const store = useStore();
const hasFetched = useMapGetter('platformTooltips/hasFetched');

// Fetch tooltips if not already loaded
onMounted(() => {
  if (!hasFetched.value) {
    store.dispatch('platformTooltips/fetch');
  }
});

const tooltip = computed(() => {
  return store.getters['platformTooltips/getByIdentifier'](props.identifier);
});

const hasTooltip = computed(() => !!tooltip.value);
const hasUrl = computed(() => {
  const url = tooltip.value?.url;
  return !!url && /^https?:\/\//i.test(url);
});

// Popover state
const showPopover = ref(false);
const popoverRef = ref(null);
const triggerRef = ref(null);

const togglePopover = () => {
  showPopover.value = !showPopover.value;
};

const closePopover = () => {
  showPopover.value = false;
};

// Close on outside click
const handleClickOutside = event => {
  if (
    popoverRef.value &&
    !popoverRef.value.contains(event.target) &&
    triggerRef.value &&
    !triggerRef.value.contains(event.target)
  ) {
    closePopover();
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});

const iconSizeClass = computed(() => {
  const sizes = {
    sm: 'size-3.5',
    md: 'size-4',
    lg: 'size-5',
  };
  return sizes[props.size];
});
</script>

<template>
  <div v-if="hasTooltip" class="relative inline-flex items-center">
    <!-- Trigger Icon -->
    <button
      ref="triggerRef"
      type="button"
      class="inline-flex items-center justify-center rounded-full text-n-slate-10 hover:text-n-brand hover:bg-n-alpha-2 transition-colors duration-150 cursor-pointer p-0.5"
      :title="$t('PLATFORM_TOOLTIP.CLICK_FOR_HELP')"
      @click.stop="togglePopover"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        :class="iconSizeClass"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    </button>

    <!-- Popover -->
    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 scale-95 translate-y-1"
      enter-to-class="opacity-100 scale-100 translate-y-0"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100 scale-100 translate-y-0"
      leave-to-class="opacity-0 scale-95 translate-y-1"
    >
      <div
        v-if="showPopover"
        ref="popoverRef"
        class="absolute z-50 top-full mt-2 ltr:left-0 rtl:right-0 w-72 bg-n-solid-2 dark:bg-n-solid-3 border border-n-weak rounded-lg shadow-lg p-4"
      >
        <!-- Tooltip Message -->
        <p class="text-sm text-n-slate-12 leading-relaxed whitespace-pre-line">
          {{ tooltip.message }}
        </p>

        <!-- Learn More Button (conditional) -->
        <a
          v-if="hasUrl"
          :href="tooltip.url"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-n-brand hover:text-n-brand/80 transition-colors"
        >
          {{ $t('PLATFORM_TOOLTIP.LEARN_MORE') }}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="size-3"
          >
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
          </svg>
        </a>

        <!-- Close hint -->
        <button
          type="button"
          class="absolute top-2 right-2 text-n-slate-10 hover:text-n-slate-12 transition-colors"
          @click="closePopover"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="size-3.5"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </Transition>
  </div>
</template>
